import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../api/axios";
import LoadingOverlay from "../../components/LoadingOverlay";

function InventoryInitialLoad() {
  const [loading, setLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState("");

  const [cart, setCart] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [clientsRes, productsRes] = await Promise.all([
        api.get("/clients"),
        api.get("/products"),
      ]);

      setClients(clientsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error(error);

      toast.error("Error cargando información.");
    } finally {
      setLoading(false);
    }
  };

  const addProduct = () => {
    if (!selectedClient) {
      toast.warning("Seleccione un área.");
      return;
    }

    if (!selectedProduct) {
      toast.warning("Seleccione un producto.");
      return;
    }

    const qty = Number(quantity);

    if (!qty || qty <= 0) {
      toast.warning("Ingrese una cantidad válida.");
      return;
    }

    const index = cart.findIndex(
      (item) => item.product_id === selectedProduct.id,
    );

    if (index >= 0) {
      const updated = [...cart];

      updated[index].quantity = Number(updated[index].quantity) + qty;

      setCart(updated);
    } else {
      setCart((prev) => [
        ...prev,
        {
          product_id: selectedProduct.id,
          name: selectedProduct.name,
          quantity: qty,
        },
      ]);
    }

    setSelectedProduct(null);
    setQuantity("");
  };

  const removeProduct = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const decreaseQuantity = (index) => {
    const updated = [...cart];

    if (updated[index].quantity <= 1) {
      removeProduct(index);
      return;
    }

    updated[index].quantity--;

    setCart(updated);
  };

  const increaseQuantity = (index) => {
    const updated = [...cart];

    updated[index].quantity++;

    setCart(updated);
  };

  const save = async () => {
    if (!selectedClient) {
      toast.warning("Seleccione un área.");
      return;
    }

    if (cart.length === 0) {
      toast.warning("Debe agregar al menos un producto.");
      return;
    }

    try {
      setLoading(true);

      const data = {
        client_id: selectedClient.value,
        products: cart.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
        })),
      };

      await api.post("/client-inventory-initial-load", data);

      toast.success("Carga inicial registrada.");

      setCart([]);
      setSelectedProduct(null);
      setQuantity("");
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.message || "Error registrando la carga inicial.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-md sm:p-6">
      <LoadingOverlay loading={loading} />

      <h2 className="mb-6 text-xl font-bold sm:text-2xl">
        Carga inicial del inventario por área
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Área</label>

          <Select
            options={clients.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            value={selectedClient}
            onChange={setSelectedClient}
            placeholder="Seleccione un área..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Producto</label>

          <Select
            options={products.map((p) => ({
              value: p.id,
              label: p.name,
              data: p,
            }))}
            value={
              selectedProduct
                ? {
                    value: selectedProduct.id,
                    label: selectedProduct.name,
                    data: selectedProduct,
                  }
                : null
            }
            onChange={(option) => setSelectedProduct(option?.data || null)}
            placeholder="Seleccione un producto..."
            isClearable
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Cantidad</label>

          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full border rounded-lg px-4 py-2"
          />
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          onClick={addProduct}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 sm:w-auto"
        >
          <Plus size={18} />
          Agregar producto
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl shadow mt-6">
        <table className="min-w-[560px] bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 uppercase text-sm text-center">
              <th className="px-3 py-3 sm:px-6">Producto</th>

              <th className="px-3 py-3 sm:px-6">Cantidad</th>

              <th className="w-32 px-3 py-3 sm:px-6">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {cart.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-8 italic text-gray-400 sm:px-6">
                  No hay productos agregados.
                </td>
              </tr>
            ) : (
              cart.map((item, index) => (
                <tr
                  key={item.product_id}
                  className={`border-t ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-3 py-4 text-center font-medium sm:px-6">
                    {item.name}
                  </td>

                  <td className="px-3 py-4 sm:px-6">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => decreaseQuantity(index)}
                        className="w-8 h-8 rounded bg-red-500 hover:bg-red-600 text-white font-bold"
                      >
                        -
                      </button>

                      <span className="w-10 text-center font-bold text-blue-600">
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => increaseQuantity(index)}
                        className="w-8 h-8 rounded bg-green-500 hover:bg-green-600 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </td>

                  <td className="px-3 py-4 sm:px-6">
                    <button
                      onClick={() => removeProduct(index)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-600 hover:text-white transition"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mt-6">
        <button
          disabled={cart.length === 0}
          onClick={save}
          className={`px-6 py-3 rounded-lg flex items-center gap-2 text-white ${
            cart.length === 0
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          <Save size={18} />
          Guardar carga inicial
        </button>
      </div>
    </div>
  );
}

export default InventoryInitialLoad;
